import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  redeemed: "bg-emerald-500/90 text-black",
  active: "bg-amber-500/90 text-black",
  expired: "bg-zinc-600 text-zinc-200",
};

export function AdminSso() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/sso-tickets");
        setData(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  const stats = data?.stats || { total: 0, redeemed: 0, active: 0, expired_unused: 0 };
  const tickets = data?.tickets || [];

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-6xl mx-auto" data-testid="admin-sso-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">SSO ticket audit</h1>
        <p className="text-sm text-zinc-500 mt-3">
          Every profit-calculator sign-in ticket: who minted it, whether it was redeemed, and when. Tickets are single-use and expire in 5 minutes — a pile of expired-unused tickets from one driver can mean a leaked link.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06] mb-8">
        <Tile label="Minted" value={stats.total} icon={KeyRound} testid="sso-stat-total" />
        <Tile label="Redeemed" value={stats.redeemed} icon={CheckCircle2} accent="text-emerald-400" testid="sso-stat-redeemed" />
        <Tile label="Active now" value={stats.active} icon={Clock} accent="text-amber-400" testid="sso-stat-active" />
        <Tile label="Expired unused" value={stats.expired_unused} icon={XCircle} accent="text-zinc-400" testid="sso-stat-expired" />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading tickets…</div>
      ) : tickets.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]" data-testid="sso-empty">
          <div className="font-display text-2xl font-black uppercase">No tickets yet</div>
          <p className="text-zinc-500 text-sm mt-2">When drivers open the profit calculator from their passport, tickets appear here.</p>
        </div>
      ) : (
        <div className="border border-white/[0.08] bg-[#0F0F10] overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/[0.06]">
                <th className="text-left p-4 font-bold">Driver</th>
                <th className="text-left p-4 font-bold">App</th>
                <th className="text-left p-4 font-bold">Status</th>
                <th className="text-left p-4 font-bold">Minted</th>
                <th className="text-left p-4 font-bold">Redeemed</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-b border-white/[0.04] last:border-0" data-testid={`sso-ticket-${t.id}`}>
                  <td className="p-4">
                    <div className="font-bold truncate">{t.driver_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{t.driver_email}</div>
                  </td>
                  <td className="p-4 text-zinc-400 text-xs uppercase tracking-widest">{t.app}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 ${STATUS_STYLE[t.status] || STATUS_STYLE.expired}`} data-testid={`sso-status-${t.id}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="p-4 text-zinc-400 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</td>
                  <td className="p-4 text-zinc-400 text-xs">{t.used_at ? new Date(t.used_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-zinc-600 mt-6">Showing the 100 most recent tickets.</p>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent = "text-zinc-300", testid }) {
  return (
    <div className="bg-[#0F0F10] p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-display text-4xl font-black uppercase mt-3 leading-none">{value}</div>
    </div>
  );
}
